import { useEffect, useState } from 'react';

import { Drawer } from '../../../components/Drawer/Drawer';
import { useToast } from '../../../components/Toast';
import { cn } from '../../../lib/cn';
import type { LeadRow } from './leads.schema';
import { useConvertLeadToCustomer } from './leadsQueries';
import styles from './leads.module.css';

const FORM_ID = 'convert-lead-form';

function suggestProjectName(lead: LeadRow): string {
  const parts = [lead.company_name?.trim(), lead.interest?.trim()].filter((p): p is string => Boolean(p));
  if (parts.length > 0) return parts.join(' · ');
  return `הצעה · ${lead.full_name}`;
}

interface ConvertResult {
  customer_id: string;
  project_id: string;
}

interface ConvertLeadModalProps {
  open: boolean;
  lead: LeadRow | null;
  orgId: string | null;
  userId: string | null;
  onClose: () => void;
  onConverted?: (result: ConvertResult) => void;
}

export function ConvertLeadModal({ open, lead, orgId, userId, onClose, onConverted }: ConvertLeadModalProps) {
  const toast = useToast();
  const convertLead = useConvertLeadToCustomer(orgId, userId);

  const [projectName, setProjectName] = useState('');
  const [valueText, setValueText] = useState('');
  const [errors, setErrors] = useState<{ project_name?: string; value_amount?: string }>({});

  useEffect(() => {
    if (!open || !lead) return;
    setProjectName(suggestProjectName(lead));
    setValueText('');
    setErrors({});
  }, [open, lead]);

  const busy = convertLead.isPending;

  function validate(): boolean {
    const next: typeof errors = {};
    if (!projectName.trim()) next.project_name = 'שדה חובה';
    if (projectName.trim().length > 120) next.project_name = 'עד 120 תווים';
    if (valueText.trim() !== '') {
      const n = Number(valueText);
      if (Number.isNaN(n) || n < 0) next.value_amount = 'מספר לא תקין';
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (!lead || !validate()) return;

    const value = valueText.trim() === '' ? null : Number(valueText);

    try {
      const result = await convertLead.mutateAsync({
        lead_id: lead.id,
        project_name: projectName,
        value_amount: value,
      });
      toast.success('הליד הומר ללקוח · נפתחה הצעה');
      onConverted?.({ customer_id: result.customer_id, project_id: result.project_id });
      onClose();
    } catch {
      toast.error('לא הצלחנו להמיר · נסה שוב');
    }
  }

  return (
    <Drawer
      open={open}
      onClose={onClose}
      eyebrow="המרה ללקוח"
      title={lead ? `המר את ${lead.full_name} ללקוח` : 'המר ללקוח'}
      ariaLabel="המרת ליד ללקוח"
      footer={
        <>
          <button type="button" className={styles.btnSecondary} disabled={busy} onClick={onClose}>
            בטל
          </button>
          <button type="submit" form={FORM_ID} className={styles.btnPrimary} disabled={busy}>
            המר ללקוח
          </button>
        </>
      }
    >
      <form
        id={FORM_ID}
        className={styles.form}
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        noValidate
      >
        <p className={styles.notice}>
          המערכת תיצור לקוח חדש (חברה + איש קשר ראשי) ופרויקט במצב «הצעה». הליד יעבור לארכיון.
        </p>

        <div className={styles.field}>
          <label htmlFor="convert-project-name">שם הפרויקט</label>
          <input
            id="convert-project-name"
            type="text"
            className={cn(styles.input, errors.project_name && styles.invalid)}
            disabled={busy}
            value={projectName}
            onChange={(e) => {
              setProjectName(e.target.value);
            }}
          />
          {errors.project_name ? <p className={styles.error}>{errors.project_name}</p> : null}
        </div>

        <div className={styles.field}>
          <label htmlFor="convert-value">ערך משוער (₪) — אופציונלי</label>
          <input
            id="convert-value"
            type="number"
            min={0}
            step="0.01"
            dir="ltr"
            className={cn(styles.input, errors.value_amount && styles.invalid)}
            disabled={busy}
            value={valueText}
            onChange={(e) => {
              setValueText(e.target.value);
            }}
          />
          {errors.value_amount ? <p className={styles.error}>{errors.value_amount}</p> : null}
        </div>
      </form>
    </Drawer>
  );
}
